import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';

import type { RouteLeg, RoutePassengerChanges, RoutePlan, RoutePlanStop } from '@/types/route-plan';
import type { TransportMode } from '@/types/transport';
import type { FamilyMember } from '@/types/family-member';
import { useFamilyMembersStore, useRoutePlanStore, useRouteGeocoder } from '@/hooks/use-scoped-managers';
import { useUserProfile } from '@/lib/user/use-user-profile';
import { useSupabaseAuth } from '@/lib/supabase';
import { deviceCalendarService } from '@/lib/calendar/DeviceCalendarService';
import { routePlanApiClient } from '@/lib/route-plan/RoutePlanApiClient';
import { routeNotificationScheduler } from '@/lib/route-plan/RouteNotificationScheduler';
import { RouteDraftFactory } from '@/lib/route-plan/RouteDraftFactory';
import {
  RouteStopCandidate,
  RouteStopCandidateBuilder,
} from '@/lib/route-plan/RouteStopCandidateBuilder';
import { RouteTimelineCalculator } from '@/lib/route-plan/RouteTimelineCalculator';
import { TasksApiClient } from '@/lib/client/TasksApiClient';

export type RouteWizardStep = 'pick' | 'locations' | 'review';
export type RouteAsyncStatus = 'idle' | 'loading' | 'error' | 'done';

function formatLocalDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function weekdayOf(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long' });
}

/**
 * useRoutePlanner — ViewModel for the route planner wizard.
 * Owns step navigation, stop candidates (events instant, note scan async,
 * to-dos), locations, the AI build call, local timeline edits, and saving.
 */
export function useRoutePlanner() {
  const { session } = useSupabaseAuth();
  const familyMembersStore = useFamilyMembersStore();
  const routePlanStore = useRoutePlanStore();
  const routeGeocoder = useRouteGeocoder();
  const { profile, updateProfile } = useUserProfile();

  const [step, setStep] = useState<RouteWizardStep>('pick');
  const [date, setDate] = useState<string>(() => formatLocalDate(new Date()));
  const [windowStart, setWindowStart] = useState<string | null>(null);
  const [windowEnd, setWindowEnd] = useState<string | null>(null);

  const [eventCandidates, setEventCandidates] = useState<RouteStopCandidate[]>([]);
  const [noteCandidates, setNoteCandidates] = useState<RouteStopCandidate[]>([]);
  const [todoCandidates, setTodoCandidates] = useState<RouteStopCandidate[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [scanStatus, setScanStatus] = useState<RouteAsyncStatus>('idle');
  const [scanError, setScanError] = useState<string | null>(null);

  const [homeAddress, setHomeAddress] = useState('');
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);

  const [buildStatus, setBuildStatus] = useState<RouteAsyncStatus>('idle');
  const [buildError, setBuildError] = useState<string | null>(null);
  const [draftStops, setDraftStops] = useState<RoutePlanStop[]>([]);
  const [draftLegs, setDraftLegs] = useState<RouteLeg[]>([]);

  const [remindersAvailable, setRemindersAvailable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Prefill home address from the profile once it loads
  useEffect(() => {
    setHomeAddress((current) => current || (profile?.homeAddress ?? ''));
  }, [profile?.homeAddress]);

  // Family members for avatars, passenger names, and the notes scan
  useEffect(() => {
    if (!familyMembersStore) return;
    familyMembersStore.getAll().then(setFamilyMembers).catch((error) => {
      console.error('[useRoutePlanner] Failed to load family members:', error);
    });
  }, [familyMembersStore]);

  // Whether departure reminders can be scheduled (for the save button label)
  useEffect(() => {
    if (Platform.OS === 'web') return;
    Notifications.getPermissionsAsync()
      .then((permissions) => setRemindersAvailable(permissions.granted))
      .catch(() => setRemindersAvailable(false));
  }, []);

  // Load calendar events for the selected date/window (instant group)
  useEffect(() => {
    let cancelled = false;
    setEventsLoading(true);
    RouteStopCandidateBuilder.loadDayEvents(date, windowStart, windowEnd)
      .then((events) => {
        if (cancelled) return;
        setEventCandidates(events.map((event) => RouteStopCandidateBuilder.fromEvent(event)));
      })
      .catch((error) => {
        console.error('[useRoutePlanner] Failed to load events:', error);
        if (!cancelled) setEventCandidates([]);
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date, windowStart, windowEnd]);

  // Load open to-dos once
  useEffect(() => {
    if (!session?.access_token || !routePlanStore) return;
    let cancelled = false;
    const tasksClient = new TasksApiClient(session.access_token);
    tasksClient
      .listTasks()
      .then(async (tasks) => {
        const open = tasks.filter((task) => !task.completed && !task.wontDo);
        const candidates = await Promise.all(
          open.map(async (task) =>
            RouteStopCandidateBuilder.fromTask(
              task,
              await routePlanStore.getRememberedLocation(task.title),
            ),
          ),
        );
        if (!cancelled) setTodoCandidates(candidates);
      })
      .catch((error) => {
        console.error('[useRoutePlanner] Failed to load to-dos:', error);
        if (!cancelled) setTodoCandidates([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token, routePlanStore]);

  // Scan family notes for obligations (async group, day-aware)
  const runScan = useCallback(async () => {
    if (!routePlanStore || familyMembers.length === 0) return;
    setScanStatus('loading');
    setScanError(null);
    try {
      const meMember = familyMembers.find((member) => member.isMe) ?? null;
      const obligations = await routePlanApiClient.scan({
        date,
        weekday: weekdayOf(date),
        windowStart,
        windowEnd,
        familyMembers: familyMembers.map((member) => ({
          id: member.id,
          name: member.name,
          notes: member.notes ?? [],
        })),
        userNotes: profile?.notes ?? [],
        meFamilyMemberId: meMember?.id ?? null,
      });
      const candidates = await Promise.all(
        obligations.map(async (obligation) =>
          RouteStopCandidateBuilder.fromObligation(
            obligation,
            await routePlanStore.getRememberedLocation(obligation.title),
          ),
        ),
      );
      setNoteCandidates(candidates);
      setScanStatus('done');
    } catch (error) {
      console.error('[useRoutePlanner] Notes scan failed:', error);
      setScanError((error as Error).message);
      setScanStatus('error');
    }
  }, [routePlanStore, familyMembers, date, windowStart, windowEnd, profile?.notes]);

  useEffect(() => {
    runScan();
  }, [runScan]);

  const updateCandidate = useCallback(
    (candidateId: string, mutate: (candidate: RouteStopCandidate) => RouteStopCandidate) => {
      const apply = (list: RouteStopCandidate[]) =>
        list.map((candidate) => (candidate.id === candidateId ? mutate(candidate) : candidate));
      setEventCandidates(apply);
      setNoteCandidates(apply);
      setTodoCandidates(apply);
    },
    [],
  );

  const toggleCandidate = useCallback(
    (candidateId: string) =>
      updateCandidate(candidateId, (candidate) => ({ ...candidate, selected: !candidate.selected })),
    [updateCandidate],
  );

  const setCandidateStart = useCallback(
    (candidateId: string, fixedStart: string) =>
      updateCandidate(candidateId, (candidate) => ({ ...candidate, fixedStart })),
    [updateCandidate],
  );

  const setCandidateLocation = useCallback(
    (candidateId: string, location: string) =>
      updateCandidate(candidateId, (candidate) => ({ ...candidate, location })),
    [updateCandidate],
  );

  const selectedCandidates = useMemo(
    () => [...eventCandidates, ...noteCandidates, ...todoCandidates].filter((c) => c.selected),
    [eventCandidates, noteCandidates, todoCandidates],
  );

  const familyMemberNames = useMemo(
    () => Object.fromEntries(familyMembers.map((member) => [member.id, member.name])),
    [familyMembers],
  );

  /**
   * Leaving the locations step: persist location edits (events → calendar,
   * note/to-do → remembered map, home → profile), then build the route.
   */
  const buildRoute = useCallback(async () => {
    if (!routePlanStore) return;
    setStep('review');
    setBuildStatus('loading');
    setBuildError(null);

    try {
      const editedEvents = eventCandidates.filter(
        (candidate) =>
          candidate.selected && candidate.sourceId && candidate.location !== candidate.originalLocation,
      );
      await Promise.all(
        editedEvents.map((candidate) =>
          deviceCalendarService.updateEvent(candidate.sourceId as string, {
            id: candidate.sourceId as string,
            location: candidate.location.trim() || null,
          }),
        ),
      );

      await routePlanStore.rememberStopLocations(
        [...noteCandidates, ...todoCandidates]
          .filter((candidate) => candidate.selected)
          .map((candidate) => ({ title: candidate.title, location: candidate.location })),
      );

      if (homeAddress.trim() && homeAddress.trim() !== (profile?.homeAddress ?? '')) {
        await updateProfile({ homeAddress: homeAddress.trim() }).catch((error) => {
          console.error('[useRoutePlanner] Failed to save home address:', error);
        });
      }

      const draftStopsRaw = RouteDraftFactory.buildStops(selectedCandidates, homeAddress);
      // Attach coordinates for the review preview; misses stay unmapped.
      const stops = routeGeocoder
        ? await routeGeocoder.geocodeStops(draftStopsRaw)
        : draftStopsRaw;
      const legs = await routePlanApiClient.build({
        date,
        weekday: weekdayOf(date),
        homeAddress: homeAddress.trim(),
        country: profile?.country ?? null,
        locale: profile?.locale ?? null,
        stops,
        familyMemberNames,
      });

      setDraftStops(stops);
      setDraftLegs(RouteTimelineCalculator.withWarnings(stops, legs));
      setBuildStatus('done');
    } catch (error) {
      console.error('[useRoutePlanner] Route build failed:', error);
      setBuildError((error as Error).message);
      setBuildStatus('error');
    }
  }, [
    routePlanStore,
    routeGeocoder,
    eventCandidates,
    noteCandidates,
    todoCandidates,
    homeAddress,
    profile?.homeAddress,
    profile?.country,
    profile?.locale,
    updateProfile,
    selectedCandidates,
    date,
    familyMemberNames,
  ]);

  const setTravelDurationMinutes = useCallback(
    (legId: string, minutes: number) => {
      setDraftLegs((legs) =>
        RouteTimelineCalculator.setTravelDuration(draftStops, legs, legId, minutes),
      );
    },
    [draftStops],
  );

  const deleteLeg = useCallback(
    (legId: string) => {
      const result = RouteTimelineCalculator.removeLeg(draftStops, draftLegs, legId);
      setDraftStops(result.stops);
      setDraftLegs(result.legs);
    },
    [draftStops, draftLegs],
  );

  const changeTravelMode = useCallback((legId: string, mode: TransportMode) => {
    setDraftLegs((legs) => RouteTimelineCalculator.setTravelMode(legs, legId, mode));
  }, []);

  const changePassengers = useCallback((legId: string, changes: RoutePassengerChanges) => {
    setDraftLegs((legs) => RouteTimelineCalculator.setPassengerChanges(legs, legId, changes));
  }, []);

  /**
   * Persist the plan (replacing any plan for the date) and schedule
   * departure notifications. Returns the saved plan.
   */
  const saveRoute = useCallback(async (): Promise<RoutePlan | null> => {
    if (!routePlanStore || draftLegs.length === 0) return null;
    setIsSaving(true);
    try {
      const plan = RouteDraftFactory.buildPlan({
        date,
        windowStart,
        windowEnd,
        stops: draftStops,
        legs: draftLegs,
      });
      await routePlanStore.savePlan(plan);
      await routeNotificationScheduler.schedulePlanNotifications(plan);
      return plan;
    } finally {
      setIsSaving(false);
    }
  }, [routePlanStore, draftLegs, draftStops, date, windowStart, windowEnd]);

  const travelLegCount = useMemo(
    () => draftLegs.filter((leg) => leg.kind === 'travel').length,
    [draftLegs],
  );

  return {
    step,
    setStep,
    date,
    setDate,
    windowStart,
    windowEnd,
    setWindowStart,
    setWindowEnd,
    eventCandidates,
    noteCandidates,
    todoCandidates,
    eventsLoading,
    scanStatus,
    scanError,
    rescan: runScan,
    toggleCandidate,
    setCandidateStart,
    setCandidateLocation,
    selectedCandidates,
    homeAddress,
    setHomeAddress,
    familyMembers,
    buildRoute,
    buildStatus,
    buildError,
    draftStops,
    draftLegs,
    setTravelDurationMinutes,
    deleteLeg,
    changeTravelMode,
    changePassengers,
    saveRoute,
    isSaving,
    remindersAvailable,
    travelLegCount,
  };
}
